import { SxProps, Theme } from "@mui/material";
import { Box, Skeleton } from "@mui/material";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { isTruthy } from "helpers/methods";
import templatesStyles from "./Templates.styles";
import templatesTokens, { templatesResponsiveTokens } from "./Templates.tokens";
// The folder dialog now renders through the app-wide Modal System, so it no
// longer exports a title or footer component to re-export here — only the
// dialog itself, its body switch and the title-text lookup.
export {
  getTitleText,
  FolderDialogBody,
  CustomDialogComponent,
} from "global/components/Folders/CustomDialogComponent";

export const popoverMenuItems = (
  type: string,
  _webFormsAvailable: boolean = true,
) => {
  return [
    ...(hasAccessTo(strings.resources.template, strings.updatePermission)
      ? [strings.rename]
      : []),
    ...(hasAccessTo(strings.resources.template, strings.deletePermission)
      ? [strings.deleteFolder]
      : []),
    ...(hasAccessTo(strings.resources.template, strings.updatePermission)
      ? [strings.moveFolder]
      : []),
    ...(hasAccessTo(strings.resources.template, strings.addPermission) &&
    type === strings.folder &&
    isTruthy(type)
      ? [strings.newSubfolder]
      : []),
    ...(hasAccessTo(strings.resources.template, strings.updatePermission) &&
    type === strings.template &&
    isTruthy(type)
      ? [strings.update]
      : []),
    ...(hasAccessTo(strings.resources.sign, strings.sendPermission) &&
    type === strings.template &&
    isTruthy(type)
      ? [strings.use]
      : []),
    ...(type === strings.template && isTruthy(type) ? ["Web Form"] : []),
  ];
};

const styles = templatesStyles;
const { card, row, kebab } = templatesTokens;
const { phone } = templatesResponsiveTokens;

/**
 * Placeholder cards matching the grid tile's chrome while a page loads.
 *
 * Every text line borrows the real card's own style: a text `Skeleton` sizes
 * its box from `fontSize` x line-height, so restating those as pixel heights
 * drifts. This one also drew three lines where the card has four (name, item
 * count, "created by", "created on"), leaving it 30px short of the card that
 * replaced it.
 */
export const renderGridSkeletons = () => {
  return Array.from({ length: 8 }).map((_, index) => (
    <Box key={index} aria-hidden="true" sx={styles.card(false)}>
      <Box sx={styles.cardHead}>
        <Skeleton
          variant="rounded"
          width={card.iconTileSize}
          height={card.iconTileSize}
          sx={{ borderRadius: card.iconTileRadius }}
        />
        <Skeleton
          variant="circular"
          width={card.kebabSize}
          height={card.kebabSize}
        />
      </Box>
      <Skeleton
        variant="text"
        sx={[styles.cardName, { width: "72%" }] as SxProps<Theme>}
      />
      <Skeleton
        variant="text"
        sx={[styles.cardSub, { width: "40%" }] as SxProps<Theme>}
      />
      <Skeleton
        variant="text"
        sx={[styles.cardCreatedBy, { width: "62%" }] as SxProps<Theme>}
      />
      <Skeleton
        variant="text"
        sx={[styles.cardCreated, { width: "56%" }] as SxProps<Theme>}
      />
    </Box>
  ));
};

/** Placeholder rows matching the list row's five-column grid. */
export const renderListSkeletons = (isMobile: boolean = false) => {
  return Array.from({ length: 8 }).map((_, index) => (
    <Box key={index} aria-hidden="true" sx={styles.listRow(false)}>
      <Box sx={styles.rowLead}>
        <Skeleton
          variant="rounded"
          width={row.iconTileSize}
          height={row.iconTileSize}
          sx={{ borderRadius: row.iconTileRadius }}
        />
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Skeleton variant="text" width={180} height={18} />
          <Skeleton variant="text" width={90} height={14} />
        </Box>
      </Box>
      <Box sx={styles.rowType}>
        <Skeleton variant="text" width={64} height={16} />
      </Box>
      <Box sx={styles.rowCreatedBy}>
        <Skeleton variant="text" width={110} height={16} />
      </Box>
      <Box sx={styles.rowCreated}>
        <Skeleton variant="text" width={150} height={16} />
      </Box>
      <Box sx={styles.kebabColumn}>
        <Skeleton variant="rounded" width={kebab.size} height={kebab.size} />
      </Box>
    </Box>
  ));
};

/** Placeholder rows matching the phone reference's compact card. */
export const renderMobileSkeletons = () => {
  return Array.from({ length: 6 }).map((_, index) => (
    <Box key={index} aria-hidden="true" sx={styles.mobileCard(false)}>
      <Skeleton
        variant="rounded"
        width={phone.card.iconTileSize.std}
        height={phone.card.iconTileSize.std}
        sx={{ borderRadius: phone.card.iconTileRadius, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="70%" height={18} />
        <Skeleton variant="text" width="45%" height={14} />
      </Box>
      <Skeleton
        variant="rounded"
        width={phone.card.kebabSize.std}
        height={phone.card.kebabSize.std}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  ));
};
